import React, { useState } from 'react'
import {
  X,
  Users,
  Mail,
  Palette,
  Check,
  Clock,
  CalendarDays,
} from 'lucide-react'
import { useAuth } from '@/lib/auth/context'
import { useTheme } from '@/lib/theme'
import { UserManagement } from './UserManagement'
import { EmailAccountsManagement } from './EmailAccountsManagement'
import { AvailabilitySettings } from './AvailabilitySettings'
import { BookingPageSettings } from './BookingPageSettings'

type SettingsTab = 'users' | 'email' | 'availability' | 'booking' | 'theme'

interface SettingsModalProps {
  isOpen: boolean
  onClose: () => void
}

export function SettingsModal({ isOpen, onClose }: SettingsModalProps) {
  const { adminUser } = useAuth()
  const { currentTheme, setTheme, themes } = useTheme()
  const [activeTab, setActiveTab] = useState<SettingsTab>('users')

  const isSuperAdmin = adminUser?.role === 'super_admin'

  if (!isOpen) return null

  const tabs: { id: SettingsTab; label: string; icon: React.ElementType; adminOnly: boolean }[] = [
    { id: 'users', label: 'Users', icon: Users, adminOnly: true },
    { id: 'email', label: 'Email Accounts', icon: Mail, adminOnly: true },
    { id: 'availability', label: 'Availability', icon: Clock, adminOnly: false },
    { id: 'booking', label: 'Booking Page', icon: CalendarDays, adminOnly: false },
    { id: 'theme', label: 'Theme', icon: Palette, adminOnly: false },
  ]

  const visibleTabs = tabs.filter(tab => !tab.adminOnly || isSuperAdmin)

  // Reset to first visible tab if current tab becomes hidden
  if (!visibleTabs.find(t => t.id === activeTab)) {
    setActiveTab(visibleTabs[0]?.id || 'theme')
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4 animate-fade-in">
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl w-full max-w-4xl max-h-[85vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[var(--border)] shrink-0">
          <div>
            <h2 className="text-xl font-bold text-[var(--text-main)]">Settings</h2>
            <p className="text-[var(--text-muted)] text-sm mt-1">
              Manage users, email accounts, and preferences
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-element)] rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-[var(--border)] px-6 shrink-0">
          {visibleTabs.map(tab => {
            const Icon = tab.icon
            const isActive = activeTab === tab.id
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  isActive
                    ? 'border-[var(--primary)] text-[var(--primary)]'
                    : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-main)]'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            )
          })}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'users' && isSuperAdmin && <UserManagement />}
          {activeTab === 'email' && isSuperAdmin && <EmailAccountsManagement />}
          {activeTab === 'availability' && <AvailabilitySettings />}
          {activeTab === 'booking' && <BookingPageSettings />}
          {activeTab === 'theme' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-[var(--text-main)] mb-2">
                  Choose Theme
                </h3>
                <p className="text-[var(--text-muted)] text-sm mb-4">
                  Select a color theme for your dashboard
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                {themes.map(theme => {
                  const isSelected = currentTheme.id === theme.id
                  return (
                    <button
                      key={theme.id}
                      onClick={() => setTheme(theme.id)}
                      className={`relative p-4 rounded-xl border-2 transition-all ${
                        isSelected
                          ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                          : 'border-[var(--border)] hover:border-[var(--text-muted)]'
                      }`}
                    >
                      <div
                        className="w-12 h-12 rounded-full mx-auto mb-3 shadow-lg"
                        style={{ backgroundColor: theme.colors.primary }}
                      />
                      <p className="text-sm font-medium text-[var(--text-main)] text-center">
                        {theme.name}
                      </p>
                      {isSelected && (
                        <div className="absolute top-2 right-2 w-5 h-5 bg-[var(--primary)] rounded-full flex items-center justify-center">
                          <Check size={12} className="text-white" />
                        </div>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
